import { useTranslation } from 'react-i18next';
import type { FieldConfig, FieldValue } from '@shared/types/interactiveFields';
import { fieldDecisionState, type DecisionState } from './fieldDecisionState';

type Language = 'bg' | 'de' | 'en';

/**
 * Оформлението на едно поле според състоянието му.
 *
 * 🔴 Защо НЕ лявата ивица, която заданието предписваше: тя иска блоков елемент.
 * Всички полета на всички декларации се рендират INLINE — вътре в изречението
 * („Социални мрежи: [ДА/НЕ]") — и там няма къде да се закачи вертикална линия.
 * Измерено на production: полетата на шестте декларации са inline без изключение.
 * Изискването остава същото — полето, което иска решение, да не се слива с
 * текста при скрол — но носителят е рамката и фонът на самия контрол.
 *
 * ⚠️ Цветът НИКОГА не е единственият сигнал: около 8% от мъжете не различават
 * червено-зелено, а декларацията се печата и като PDF. Затова всяко състояние
 * носи и ИКОНКА, а при `answered` самият отговор се вижда в контрола.
 */
const STATE_STYLES: Record<DecisionState, { control: string; icon: string; iconClass: string }> = {
  // Единственото цветно на екрана — окото го намира, без да чете.
  pending: {
    control: 'border-amber-400 bg-amber-50 ring-1 ring-amber-300 focus:ring-amber-500',
    icon: '●',
    iconClass: 'text-amber-600',
  },
  // Присъства, не крещи: без фон, за да изчезне от вниманието.
  answered: {
    control: 'border-green-500 focus:ring-green-500',
    icon: '✓',
    iconClass: 'text-green-600',
  },
  error: {
    control: 'border-red-500 bg-red-50 focus:ring-red-500',
    icon: '!',
    iconClass: 'text-red-600 font-bold',
  },
  optional: {
    control: 'border-gray-300 focus:ring-blue-500',
    icon: '',
    iconClass: '',
  },
};

interface InlineFieldRendererProps {
  field: FieldConfig;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  error?: string;
  disabled?: boolean;
  language: Language;
}

/**
 * Inline field renderer - renders fields inline within text (compact style)
 * For use in declaration text with placeholders
 */
export function InlineFieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled,
  language,
}: InlineFieldRendererProps) {
  const getLocalizedText = (text: string | { bg: string; de?: string; en?: string } | undefined): string => {
    if (!text) return '';
    if (typeof text === 'string') return text;
    return text[language] || text.bg || '';
  };

  const { t } = useTranslation();
  const label = getLocalizedText(field.label);
  const placeholder = getLocalizedText(field.placeholder);

  // For table fields, don't render inline - they're too complex
  if (field.type === 'table') {
    return (
      <span className="inline-block px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm font-medium">
        [{t('declarationFields.tablePlaceholder')}: {label}]
      </span>
    );
  }

  const state = fieldDecisionState(field, value, error);
  const style = STATE_STYLES[state];

  /**
   * Знакът пред контрола — вторият сигнал до цвета.
   *
   * `aria-label` носи ТЕКСТА на състоянието, защото знакът сам („●") не значи
   * нищо за екранен четец. Трите заедно — цвят, знак, текст — работят и на
   * черно-бял печат.
   */
  const marker = style.icon ? (
    <span
      className={`mr-1 text-sm select-none ${style.iconClass}`}
      aria-label={t(`declarationFields.state.${state}`)}
      title={t(`declarationFields.state.${state}`)}
      data-decision-state={state}
    >
      {style.icon}
    </span>
  ) : null;

  // Common inline input styles
  const inputClassName = `inline-block min-w-[200px] px-2 py-1 text-sm border rounded ${
    style.control
  } focus:ring-2 focus:outline-none ${disabled ? 'bg-gray-100' : state === 'pending' ? '' : 'bg-white'}`;

  switch (field.type) {
    case 'text':
    case 'email':
    case 'tel':
      return (
        <span className="inline-block">
          {marker}
          <input
            type={field.type}
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || label}
            disabled={disabled}
            className={inputClassName}
            title={label}
          />
          {error && (
            <span className="ml-1 text-xs text-red-600" title={error}>
              *
            </span>
          )}
        </span>
      );

    case 'textarea':
      return (
        <span className="inline-block">
          {marker}
          <textarea
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || label}
            disabled={disabled}
            rows={field.rows || 3}
            className={`${inputClassName} align-top`}
            title={label}
          />
          {error && (
            <span className="ml-1 text-xs text-red-600" title={error}>
              *
            </span>
          )}
        </span>
      );

    case 'date':
      return (
        <span className="inline-block">
          {marker}
          <input
            type="date"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={inputClassName}
            title={label}
          />
          {error && (
            <span className="ml-1 text-xs text-red-600" title={error}>
              *
            </span>
          )}
        </span>
      );

    case 'select':
      return (
        <span className="inline-block">
          {marker}
          <select
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={inputClassName}
            title={label}
          >
            <option value="">{placeholder || t('declarationFields.choose', { label })}</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {getLocalizedText(option.label)}
              </option>
            ))}
          </select>
          {error && (
            <span className="ml-1 text-xs text-red-600" title={error}>
              *
            </span>
          )}
        </span>
      );

    case 'checkbox':
      return (
        <span className="inline-flex items-center gap-1">
          {marker}
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={(value as boolean) || false}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-1 text-sm text-gray-700">{label}</span>
          </label>
          {error && (
            <span className="ml-1 text-xs text-red-600" title={error}>
              *
            </span>
          )}
        </span>
      );

    default:
      return (
        <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm">
          [{label}]
        </span>
      );
  }
}
